import { useMemo } from "react";
import type { IExtendedSearchItem } from "src/api/client/extended-search.api.types";
import { SEARCH_TABLE_FIRST_COLUMN_WIDTH, SEARCH_TABLE_PAGE_SIZE_OPTIONS } from "src/components/pages/search/components/table/consts";
import { makeSearchTableColumns } from "src/components/pages/search/components/table/helpers/make-search-table-columns";
import { makeSearchTableLoadingColumns } from "src/components/pages/search/components/table/helpers/make-search-table-loading-columns";
import { ISearchTableProps } from "src/components/pages/search/components/table/search-table.types";
import { hasOutperformanceTags } from "src/components-mui/base/table-scrollable/components/entity/tag-icon";
import { TablePagination } from "src/components-mui/base/table-scrollable/components/pagination/table-pagination";
import { MuiTableScrollable } from "src/components-mui/base/table-scrollable/table-scrollable";
import { getTableDataForLoading } from "src/components-mui/base/table-scrollable/utils";

const SEARCH_TABLE_SCROLL_TOP_ADDITIONAL_OFFSET = 80;
export const SearchTable = ({
    data,
    figures,
    isLoading,
    sorting,
    onSortingChange,
    pagination,
    onPaginationChange,
    rowCount,
    pageSizeOptions = SEARCH_TABLE_PAGE_SIZE_OPTIONS,
    itemsLabel,
}: ISearchTableProps) => {
    const columns = useMemo(
        () =>
            makeSearchTableColumns(
                figures,
                data.some(item => hasOutperformanceTags(item.tags))
            ),
        [figures, data]
    );
    const loadingColumns = useMemo(() => makeSearchTableLoadingColumns({ figures: figures ?? [], sorting: true }), [figures]);

    return (
        <MuiTableScrollable
            data={isLoading ? (getTableDataForLoading(pagination.pageSize, pagination.pageIndex, rowCount) as IExtendedSearchItem[]) : data}
            columns={isLoading ? loadingColumns : columns}
            sorting={sorting}
            onSortingChange={onSortingChange}
            resetScrollKey={figures}
            firstColumnWidth={SEARCH_TABLE_FIRST_COLUMN_WIDTH}
            pagination={pagination}
            onPaginationChange={onPaginationChange}
            manualPagination
            manualSorting
            rowCount={rowCount}
            scrollToTableTopOnPagination
            scrollToTableTopOffset={SEARCH_TABLE_SCROLL_TOP_ADDITIONAL_OFFSET}
            paginationRender={table => (
                <TablePagination
                    pageSize={table.getState().pagination.pageSize}
                    currentPageIndex={table.getState().pagination.pageIndex}
                    pageIndexes={table.getPageOptions()}
                    onPageSizeChange={table.setPageSize}
                    onPageSelect={table.setPageIndex}
                    canGoPreviousPage={table.getCanPreviousPage()}
                    canGoNextPage={table.getCanNextPage()}
                    onPreviousClick={table.previousPage}
                    onNextClick={table.nextPage}
                    pageSizeOptions={pageSizeOptions}
                    itemsCount={rowCount}
                    itemsLabel={itemsLabel}
                    isLoading={isLoading}
                    hidePageNumbers
                />
            )}
        />
    );
};
